import { Link } from "@inertiajs/react";
import MonitoredJourneyChip from "@/components/domains/MonitoredJourneyChip";
import MonitoredPageCheckSchedule from "@/components/domains/MonitoredPageCheckSchedule";
import type { MonitoredJourneySummary, MonitoredPageSummary } from "@/types";

type Props = {
  journeys: MonitoredJourneySummary[];
  pages: MonitoredPageSummary[];
  canManage: boolean;
  onManage: () => void;
};

export default function MonitoredPagesStrip({ journeys, pages, canManage, onManage }: Props) {
  const empty = journeys.length === 0 && pages.length === 0;

  return (
    <section className="dashboard-section">
      <div className="dashboard-section-body">
        <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Monitoring</h2>
            <p className="text-sm text-gray-500">Journeys and pages we recheck weekly for UX issues</p>
          </div>
          {canManage ? (
            <button type="button" className="btn" onClick={onManage}>
              Manage monitoring
            </button>
          ) : null}
        </div>

        {canManage ? (
          empty ? (
            <p className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
              No journeys or pages are monitored weekly yet.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {journeys.map((journey) => (
                <MonitoredJourneyChip key={journey.id} journey={journey} />
              ))}
              {pages.map((page) => (
                <Link
                  key={page.id}
                  href={page.page_url}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm transition hover:border-primary hover:bg-primary-50"
                >
                  <span className="font-medium text-gray-900">{page.title || page.path}</span>
                  <span className="mt-0.5 block text-gray-500">{page.path}</span>
                  <MonitoredPageCheckSchedule
                    last_checked_at={page.last_checked_at}
                    next_check_at={page.next_check_at}
                  />
                </Link>
              ))}
            </div>
          )
        ) : (
          <div className="flex flex-col justify-between gap-4 rounded-lg border border-primary/20 bg-primary/5 px-5 py-4 sm:flex-row sm:items-center">
            <div>
              <h3 className="font-semibold text-gray-900">Keep journeys and pages fresh</h3>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-600">
                Upgrade to cover journeys after they log in with a test account you provide, plus weekly UX checks of
                the pages and journeys you monitor.
              </p>
            </div>
            <Link href="/subscription" className="btn btn-primary w-fit shrink-0">
              View plans
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
